import { useEffect, useState, useRef, useMemo } from 'react';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import ClientForm from './ClientForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';

// One key per filterable column; each maps to a same-named query param on
// GET /clients.
const EMPTY_FILTER = { name: '', status: '', invoiced: '', accessCode: '' };

const STATUS_OPTIONS = ['Active', 'Inactive'];

function ClientTable() {
  const toast = useToast();
  const {
    rows: clients, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/clients', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'id', direction: 'asc' } });

  // Drawer state: null when closed, { mode: 'create' } or { mode: 'edit', id } when open
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (client) => setDrawer({ mode: 'edit', id: client.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(c => ({
        Name: c.name, Status: c.status, 'Start Date': c.startDate,
        Invoiced: c.invoiced, 'Access Code': c.accessCode,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Clients');
      XLSX.writeFile(wb, 'clients.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Column catalogue in default order. Name is locked because it is the row's
  // open link and the record's identity.
  const columnCatalogue = useMemo(() => [
    {
      key: 'name', label: 'Name', locked: true,
      filter: { name: 'name' },
      cell: c => (
        <td>
          <button type="button" className="btn-link table-link" onClick={() => openEdit(c)} title="Open client">
            {c.name}
          </button>
        </td>
      ),
    },
    {
      key: 'status', label: 'Status',
      filter: { name: 'status', options: STATUS_OPTIONS },
      cell: c => <td><StatusBadge status={c.status} /></td>,
    },
    {
      key: 'startDate', label: 'Start Date',
      cell: c => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(c.startDate)}</td>,
    },
    {
      key: 'invoiced', label: 'Invoiced',
      filter: { name: 'invoiced', options: [{ value: 'true', label: 'Invoiced only' }] },
      cell: c => (
        <td>
          {Boolean(c.invoiced)
            ? <span className="badge badge-success">Yes</span>
            : <span className="badge badge-neutral">No</span>}
        </td>
      ),
    },
    {
      key: 'accessCode', label: 'Access Code',
      filter: { name: 'accessCode' },
      cell: c => <td><code className="code-pill">{c.accessCode}</code></td>,
    },
    // openEdit closes over setDrawer, stable for this table's life
  ], []);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('clients', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Clients"
        subtitle="Manage client accounts and invoicing contacts"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Client
            </button>
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && clients.length > 0 ? 0.55 : 1 }}>
            {clients.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No clients yet"
                emptyHint="Add your first client to start orders, timesheets, and invoicing."
                emptyAction={{ label: 'New Client', onClick: openCreate }}
              />
            ) : clients.map(c => (
              <tr key={c.id}>
                <ColumnCells columns={columns} row={c} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit client' : 'New client'}
        subtitle={drawer?.mode === 'edit' ? 'Update client info and contacts.' : 'Add a client in two steps: details, contacts.'}
        footer={
          drawer?.mode === 'edit' ? (
            <>
              <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
              <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
                Save changes
              </button>
            </>
          ) : (
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
          )
        }
      >
        {drawer && (
          <ClientForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            clientId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default ClientTable;
